import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, CircularProgress, Button, Divider, Chip,
    Card, CardContent, Table, TableHead, TableBody, TableCell, TableContainer, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Bolt, AccessTime, Edit, LocalTaxi, Public, Map, Save, Place, Route, MyLocation, Loyalty
} from '@mui/icons-material';
import {
    getZones, getZoneRates, upsertZoneRate, updateZone, getZoneById,
    getZoneSubscriptionPlans, upsertZoneSubscriptionOverride,
    Zone, ZoneRateItem, ZoneSubscriptionPlan
} from '../services/zoneService';

const ZoneDetails: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Data State
    const [zone, setZone] = useState<Zone | null>(null);
    const [rates, setRates] = useState<ZoneRateItem[]>([]);
    const [subPlans, setSubPlans] = useState<ZoneSubscriptionPlan[]>([]);
    const [loading, setLoading] = useState(true);

    // Edit Rate Modal State
    const [rateModalOpen, setRateModalOpen] = useState(false);
    const [selectedRate, setSelectedRate] = useState<ZoneRateItem | null>(null);
    const [rateForm, setRateForm] = useState({
        baseFare: "",
        perKm: "",
    });

    const [subModalOpen, setSubModalOpen] = useState(false);
    const [selectedSub, setSelectedSub] = useState<ZoneSubscriptionPlan | null>(null);
    const [subForm, setSubForm] = useState({
        price: "",
        maxRides: "",
        isAvailable: true
    });

    const [mapConfigOpen, setMapConfigOpen] = useState(false);
    const [mapConfigForm, setMapConfigForm] = useState({
        geocodeProvider: "",
        placesProvider: "",
        routeProvider: ""
    });

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        if (!id) return;
        try {
            setLoading(true);

            // FIX: Fetch specific zone directly from API
            const [zoneRes, ratesRes, subsRes] = await Promise.all([
                getZoneById(Number(id)),
                getZoneRates(Number(id)),
                getZoneSubscriptionPlans(Number(id))
            ]);

            setZone(zoneRes.data);
            setRates(ratesRes.data);

            // SAFE ARRAY EXTRACTION: Handle {success:true, data:[]} vs []
            const subsData = subsRes.data;
            if (Array.isArray(subsData)) {
                setSubPlans(subsData);
            } else if (subsData && Array.isArray((subsData as any).data)) {
                setSubPlans((subsData as any).data);
            } else {
                setSubPlans([]); // Fallback to empty array to prevent .map crash
            }

        } catch (err) {
            console.error("Failed to fetch zone details", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditRate = (rate: ZoneRateItem) => {
        setSelectedRate(rate);
        setRateForm({
            baseFare: (rate.baseFare !== null && rate.baseFare !== undefined) ? String(rate.baseFare) : "", // Empty means "Use Global"
            perKm: (rate.perKm !== null && rate.perKm !== undefined) ? String(rate.perKm) : "",
        });
        setRateModalOpen(true);
    };

    const handleSaveRate = async () => {
        if (!selectedRate || !zone) return;
        try {
            await upsertZoneRate(zone.id, {
                rideOptionId: selectedRate.rideOptionId,
                baseFare: rateForm.baseFare ? parseFloat(rateForm.baseFare) : undefined,
                perKm: rateForm.perKm ? parseFloat(rateForm.perKm) : undefined,
                // Add pickup charges logic if needed
            });
            setRateModalOpen(false);
            // Refresh rates
            const ratesRes = await getZoneRates(zone.id);
            setRates(ratesRes.data);
        } catch (error) {
            console.error("Failed to save rate", error);
        }
    };

    const handleEditSub = (sub: ZoneSubscriptionPlan) => {
        setSelectedSub(sub);
        setSubForm({
            price: (sub.overridePrice !== null && sub.overridePrice !== undefined) ? String(sub.overridePrice) : "",
            maxRides: (sub.overrideMaxRides !== null && sub.overrideMaxRides !== undefined) ? String(sub.overrideMaxRides) : "",
            isAvailable: sub.isAvailable
        });
        setSubModalOpen(true);
    };

    const handleSaveSub = async () => {
        if (!selectedSub || !zone) return;
        try {
            const priceVal = subForm.price.trim() === "" ? null : parseFloat(subForm.price);
            const ridesVal = subForm.maxRides.trim() === "" ? null : parseInt(subForm.maxRides);

            await upsertZoneSubscriptionOverride(zone.id, {
                planId: selectedSub.planId,
                price: priceVal,
                maxRides: ridesVal,
                isAvailable: subForm.isAvailable
            });

            setSubModalOpen(false);

            // Refresh Subscription Data
            const subsRes = await getZoneSubscriptionPlans(zone.id);
            const subsData = subsRes.data;
            if (Array.isArray(subsData)) {
                setSubPlans(subsData);
            } else if (subsData && Array.isArray((subsData as any).data)) {
                setSubPlans((subsData as any).data);
            } else {
                setSubPlans([]);
            }

        } catch (error) {
            console.error("Failed to save subscription override", error);
        }
    };

    const handleEditMapConfig = () => {
        if (!zone) return;
        setMapConfigForm({
            geocodeProvider: zone.geocodeProvider || "",
            placesProvider: zone.placesProvider || "",
            routeProvider: zone.routeProvider || ""
        });
        setMapConfigOpen(true);
    };

    const handleSaveMapConfig = async () => {
        if (!zone) return;
        try {
            await updateZone(zone.id, {
                geocodeProvider: mapConfigForm.geocodeProvider || null,
                placesProvider: mapConfigForm.placesProvider || null,
                routeProvider: mapConfigForm.routeProvider || null
            });
            setMapConfigOpen(false);
            fetchData(); // Refresh zone data to show new chips
        } catch (error) {
            console.error("Failed to save map config", error);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (!zone) return <Box sx={{ p: 3 }}><Typography>Zone not found</Typography></Box>;

    const InfoRow = ({ label, value, icon }: { label: string, value: any, icon?: React.ReactNode }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {value}
            </Typography>
        </Box>
    );

    // 2. Helper for Provider Display
    const ProviderChip = ({ type, value }: { type: string, value?: string | null }) => {
        const isGlobal = !value;
        const isOsm = value === 'osm';
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">{type}</Typography>
                <Chip
                    label={isGlobal ? "Global" : value?.toUpperCase()}
                    color={isOsm ? "info" : "default"}
                    size="small"
                    variant={isGlobal ? "outlined" : "filled"}
                />
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2, textTransform: 'none' }}
            >
                Back to Zone List
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {zone.name}
                    </Typography>
                    <Chip
                        label={zone.isActive ? "ACTIVE" : "INACTIVE"}
                        color={zone.isActive ? 'success' : 'default'}
                        variant="filled"
                        size="small"
                    />
                </Box>
                <Chip icon={<Map />} label={`ID: ${zone.id}`} variant="outlined" />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>

                {/* LEFT COLUMN: Config (30%) */}
                <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
                    {/* 3. Map Provider Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Public color="primary" /> Map Providers
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <ProviderChip type="Geocoding" value={zone.geocodeProvider} />
                            <ProviderChip type="Places Search" value={zone.placesProvider} />
                            <ProviderChip type="Routing" value={zone.routeProvider} />

                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{ mt: 2 }}
                                startIcon={<Edit />}
                                onClick={handleEditMapConfig}
                            >
                                Configure Providers
                            </Button>
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Bolt color="warning" /> Surge Configuration
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <InfoRow
                                label="Base Surge Multiplier"
                                value={`${zone.surgeMultiplier}x`}
                            />
                            <InfoRow
                                label="Rush Hour Multiplier"
                                value={`${zone.rushHourMultiplier || 1.0}x`}
                            />
                            <InfoRow
                                label="Rush Hour Window"
                                value={zone.rushHourStart ? `${zone.rushHourStart}:00 to ${zone.rushHourEnd}:00` : "Not Configured"}
                                icon={<AccessTime fontSize="small" color="action"/>}
                            />

                            <Button variant="outlined" fullWidth sx={{ mt: 2 }} disabled>
                                Edit Config (Use List View)
                            </Button>
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Loyalty color="secondary" /> Subscriptions
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box>
                                {subPlans.map(plan => (
                                    <Box key={plan.planId} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 0, mb: 0 } }}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5}}>
                                            <Typography variant="subtitle2" sx={{fontWeight: 'bold', color: plan.isAvailable ? 'text.primary' : 'text.disabled'}}>
                                                {plan.name}
                                            </Typography>
                                            <Button size="small" onClick={() => handleEditSub(plan)} sx={{minWidth: 0, p: 0.5}}>Edit</Button>
                                        </Box>

                                        {!plan.isAvailable ? (
                                            <Chip label="Disabled in Zone" size="small" color="error" variant="outlined" />
                                        ) : (
                                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                                <Chip
                                                    label={`₹${plan.overridePrice ?? plan.globalPrice}`}
                                                    size="small"
                                                    color={plan.overridePrice ? "secondary" : "default"}
                                                    variant={plan.overridePrice ? "filled" : "outlined"}
                                                />
                                                <Chip
                                                    label={plan.overrideMaxRides ?? plan.globalMaxRides ?? "Unlimited" + " Rides"}
                                                    size="small"
                                                    color={plan.overrideMaxRides !== null ? "secondary" : "default"}
                                                    variant={plan.overrideMaxRides !== null ? "filled" : "outlined"}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                                {subPlans.length === 0 && (
                                    <Typography variant="caption" color="textSecondary">No subscription plans found.</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* RIGHT COLUMN: Pricing (70%) */}
                <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
                    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocalTaxi color="primary" /> Zone Pricing Overrides
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Vehicles without overrides use Global pricing (shown in grey).
                            </Typography>
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                        <TableCell>Vehicle Type</TableCell>
                                        <TableCell>Base Fare</TableCell>
                                        <TableCell>Per Km</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rates.map((rate) => {
                                        const isCustom = rate.isOverridden;
                                        return (
                                            <TableRow key={rate.rideOptionId} hover>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {rate.rideName}
                                                    <Typography variant="caption" display="block" color="textSecondary">
                                                        {rate.rideType}
                                                    </Typography>
                                                </TableCell>

                                                {/* Base Fare Column */}
                                                <TableCell>
                                                    {isCustom ? (
                                                        <Typography color="primary.main" fontWeight="bold">₹{rate.baseFare}</Typography>
                                                    ) : (
                                                        <Typography color="textSecondary">₹{rate.globalBaseFare} (Global)</Typography>
                                                    )}
                                                </TableCell>

                                                {/* Per Km Column */}
                                                <TableCell>
                                                    {isCustom ? (
                                                        <Typography color="primary.main" fontWeight="bold">₹{rate.perKm}/km</Typography>
                                                    ) : (
                                                        <Typography color="textSecondary">₹{rate.globalPerKm}/km (Global)</Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {isCustom ? (
                                                        <Chip label="Overridden" color="primary" size="small" variant="outlined" />
                                                    ) : (
                                                        <Chip label="Default" size="small" />
                                                    )}
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={<Edit />}
                                                        onClick={() => handleEditRate(rate)}
                                                    >
                                                        Edit
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </Box>

            {/* EDIT RATE DIALOG */}
            <Dialog open={rateModalOpen} onClose={() => setRateModalOpen(false)}>
                <DialogTitle>
                    Edit Pricing: {selectedRate?.rideName}
                </DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 350 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Leave fields blank to revert to Global Pricing.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Global Reference Box */}
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" display="block">Global Base</Typography>
                            <Typography variant="h6">₹{selectedRate?.globalBaseFare}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" display="block">Global Per Km</Typography>
                            <Typography variant="h6">₹{selectedRate?.globalPerKm}</Typography>
                        </Box>
                    </Box>

                    <TextField
                        label="Zone Base Fare Override"
                        type="number"
                        value={rateForm.baseFare}
                        onChange={(e) => setRateForm({ ...rateForm, baseFare: e.target.value })}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Cost for the first few kms"
                    />
                    <TextField
                        label="Zone Per Km Override"
                        type="number"
                        value={rateForm.perKm}
                        onChange={(e) => setRateForm({ ...rateForm, perKm: e.target.value })}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRateModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveRate}
                        startIcon={<Save />}
                    >
                        Save Price
                    </Button>
                </DialogActions>
            </Dialog>

            {/* NEW: EDIT SUBSCRIPTION DIALOG */}
            <Dialog open={subModalOpen} onClose={() => setSubModalOpen(false)}>
                <DialogTitle>Configure Plan: {selectedSub?.name}</DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 350 }}>
                    <FormControlLabel
                        control={<Switch checked={subForm.isAvailable} onChange={(e) => setSubForm({...subForm, isAvailable: e.target.checked})} />}
                        label={subForm.isAvailable ? "Available in this Zone" : "Disabled in this Zone"}
                    />

                    <Divider />
                    <Typography variant="body2" color="textSecondary">Override pricing (Leave blank for global default)</Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}><Typography variant="caption" display="block">Global Price</Typography><Typography variant="h6">₹{selectedSub?.globalPrice}</Typography></Box>
                        <Box sx={{ flex: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}><Typography variant="caption" display="block">Global Limit</Typography><Typography variant="h6">{selectedSub?.globalMaxRides ?? 'Unltd'}</Typography></Box>
                    </Box>

                    <TextField
                        label="Zone Specific Price"
                        type="number"
                        value={subForm.price}
                        onChange={(e) => setSubForm({ ...subForm, price: e.target.value })}
                        fullWidth
                        disabled={!subForm.isAvailable}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                    <TextField
                        label="Zone Specific Ride Limit"
                        type="number"
                        value={subForm.maxRides}
                        onChange={(e) => setSubForm({ ...subForm, maxRides: e.target.value })}
                        fullWidth
                        disabled={!subForm.isAvailable}
                        helperText="Enter 0 or leave blank to use global"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveSub} startIcon={<Save />}>Save Config</Button>
                </DialogActions>
            </Dialog>

            {/* EDIT MAP CONFIG DIALOG */}
            <Dialog open={mapConfigOpen} onClose={() => setMapConfigOpen(false)}>
                <DialogTitle>Configure Map Providers</DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 350 }}>
                    <Typography variant="body2" color="textSecondary">
                        Override global settings for this specific zone. <br/>
                        <strong>OSM</strong> is cheaper/free. <strong>Google</strong> is more accurate.
                    </Typography>

                    {/* Geocoding Provider */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Geocoding</InputLabel>
                        <Select
                            value={mapConfigForm.geocodeProvider}
                            label="Geocoding (Address Lookup)"
                            onChange={(e) => setMapConfigForm({ ...mapConfigForm, geocodeProvider: e.target.value })}
                        >
                            <MenuItem value=""><em>Global Default</em></MenuItem>
                            <MenuItem value="google">Google</MenuItem>
                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Places Provider */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Places Search</InputLabel>
                        <Select
                            value={mapConfigForm.placesProvider}
                            label="Places Search (Autocomplete)"
                            onChange={(e) => setMapConfigForm({ ...mapConfigForm, placesProvider: e.target.value })}
                        >
                            <MenuItem value=""><em>Global Default</em></MenuItem>
                            <MenuItem value="google">Google Places</MenuItem>
                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Routing Provider */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Routing</InputLabel>
                        <Select
                            value={mapConfigForm.routeProvider}
                            label="Routing (Path & Distance)"
                            onChange={(e) => setMapConfigForm({ ...mapConfigForm, routeProvider: e.target.value })}
                        >
                            <MenuItem value=""><em>Global Default</em></MenuItem>
                            <MenuItem value="google">Google Directions</MenuItem>
                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                        </Select>
                    </FormControl>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMapConfigOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveMapConfig}
                        startIcon={<Save />}
                    >
                        Save Config
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoneDetails;